/** @format */
import { useSpring } from '@react-spring/web';
import { MutableRefObject, useEffect } from 'react';

import { ProductBoxProps } from '../types';

/**
 * Gets the animated style for the product box
 *
 * @format
 * @param boxListRef A ref to product box list
 * @param productBoxProps ProductBoxProps
 * @returns Animated react-spring style properties
 */

export const useBoxListAnimation = (
    boxListRef: MutableRefObject<HTMLElement>,
    {
        isCollapsable,
        isCollapsed,
    }: Pick<ProductBoxProps, 'isCollapsable' | 'isCollapsed'>
) => {
    const getBoxListSpringHeight = () => {
        if (isCollapsable && isCollapsed) {
            return 0;
        }

        return boxListRef?.current?.scrollHeight;
    };

    const [boxListAnimatedContainerStyle, boxListAnimatedContainerAPI] =
        useSpring(() => ({
            height: getBoxListSpringHeight(),
        }));

    const setBoxListSpringHeight = () => {
        try {
            boxListAnimatedContainerAPI.set({
                height: getBoxListSpringHeight(),
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy code: do not use as an example.
        } catch (error) {
            return;
        }
    };

    const animateBoxListSpringHeight = () => {
        try {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Legacy code: do not use as an example.
            boxListAnimatedContainerAPI.start({
                height: getBoxListSpringHeight(),
            });
            // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Legacy code: do not use as an example.
        } catch (error) {
            return;
        }
    };

    /**
     * set the box list height spring to it's saved state for collapsing / non-collapsing
     */
    useEffect(() => {
        setBoxListSpringHeight();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [isCollapsable]);

    /**
     * set the box list spring height to the box list height when the inner content height changes
     */
    useEffect(() => {
        if (!isCollapsable) {
            setBoxListSpringHeight();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [boxListRef?.current?.scrollHeight]);

    /**
     * animate to the new height when visibility is toggled
     */
    useEffect(() => {
        animateBoxListSpringHeight();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Legacy code: do not use as an example.
    }, [isCollapsed]);

    return boxListAnimatedContainerStyle;
};
